import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";
import { REPORT_SNAPSHOT_DROPZONE_ACCEPT } from "./snapshotDropzone.utils";

interface SnapshotDropzoneAreaProps {
  onDrop: (accepted: File[], rejections: FileRejection[]) => void;
}

/** A section's drop target (click to choose, too); disabled while the section builds. */
export const SnapshotDropzoneArea = (props: SnapshotDropzoneAreaProps) => {
  const { onDrop } = props;
  const section = useSnapshotSection();
  const disabled = section.isBuilding;

  const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
    onDrop(accepted, rejections);
    const input = dropzone.inputRef.current;
    if (input) input.value = "";
  };

  const dropzone = useDropzone({
    accept: REPORT_SNAPSHOT_DROPZONE_ACCEPT,
    multiple: true,
    disabled,
    onDrop: handleDrop,
  });
  const isDragActive = dropzone.isDragActive;

  let borderColor = "transparent";
  let bgcolor = "action.hover";
  if (isDragActive) {
    borderColor = "primary.main";
    bgcolor = "surface.info";
  }

  return (
    <Stack
      {...dropzone.getRootProps()}
      direction="row"
      sx={{
        alignItems: "center",
        bgcolor,
        border: 1,
        borderColor,
        borderRadius: 1,
        borderStyle: isDragActive ? "solid" : "dashed",
        color: "text.secondary",
        cursor: disabled ? "default" : "pointer",
        gap: 1.5,
        minHeight: 52,
        opacity: disabled ? 0.5 : 1,
        pr: 2,
        pl: 0.55,
        py: 1.5,
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": disabled ? undefined : {
          bgcolor: "surface.info",
          borderColor: "primary.main",
          "& .SnapshotDropzoneArea-icon": { color: "primary.main" },
        },
        "&:focus-within": { borderColor: "primary.main" },
      }}
    >
      <input {...dropzone.getInputProps()} />
      <FileUploadOutlinedIcon
        className="SnapshotDropzoneArea-icon"
        fontSize="small"
        sx={{ color: isDragActive ? "primary.main" : "text.disabled" }}
      />
      <Typography
        sx={{ minWidth: 0 }}
        variant="body2"
      >
        <Typography
          component="strong"
          sx={{
            color: "text.primary",
            fontWeight: 600,
          }}
          variant="inherit"
        >
          Drop {section.label} exports
        </Typography>
        {" "}or click to choose
      </Typography>
      <Typography
        color="text.disabled"
        sx={{
          ml: "auto",
          whiteSpace: "nowrap",
        }}
        variant="caption"
      >
        .xls · .xlsx · .csv
      </Typography>
    </Stack>
  );
};
